import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, FileText, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronUp} from 'lucide-react'
import { lumi } from '../lib/lumi'

export default function NotaFiscal() {
  const navigate = useNavigate()
  const [tipoEmissor, setTipoEmissor] = useState('')

  useEffect(() => {
    carregarHistorico()
  }, [])

  const carregarHistorico = async () => {
    try {
      const user = lumi.auth.user
      if (!user) return

      const result = await lumi.entities.configuracao_marca.list({
        filter: { userId: user.userId, tipo: 'orientacao_nota_fiscal' },
        sort: { createdAt: -1 }
      })
      setHistorico(result.list || [])
    } catch (error) {
      console.error('Erro ao carregar histórico:', error)
    }
  }
  const [tipoServico, setTipoServico] = useState('')
  const [valor, setValor] = useState('')
  const [loading, setLoading] = useState(false)
  const [orientacao, setOrientacao] = useState<any>(null)
  const [historico, setHistorico] = useState<any[]>([])
  const [expandido, setExpandido] = useState<string | null>(null)

  const getCNAE = (servico: string) => {
    const servicos: any = {
      'desenvolvimento': '6201-5/00 - Desenvolvimento de software sob encomenda',
      'design': '7410-2/02 - Design gráfico',
      'marketing': '7311-4/00 - Agências de publicidade',
      'consultoria': '7020-4/00 - Consultoria empresarial',
      'fotografia': '7420-0/01 - Atividades de produção fotográfica',
      'advocacia': '6911-7/01 - Serviços advocatícios',
      'contabilidade': '6920-6/01 - Atividades de contabilidade',
      'arquitetura': '7111-1/00 - Serviços de arquitetura'
    }
    
    const key = Object.keys(servicos).find(k => servico.toLowerCase().includes(k))
    return key ? servicos[key] : '8599-6/04 - Treinamento em desenvolvimento profissional e gerencial'
  }

  const calcularImposto = (tipoEmissor: string, valor: number) => {
    switch(tipoEmissor) {
      case 'MEI':
        return { percentual: 0, valor: 0, tipo: 'Isento' }
      case 'Simples Nacional':
        const taxa = valor <= 15000 ? 6 : valor <= 30000 ? 8.21 : 10.26
        return { percentual: taxa, valor: (valor * taxa) / 100, tipo: 'Simples Nacional' }
      case 'Lucro Presumido':
        return { percentual: 13.33, valor: (valor * 13.33) / 100, tipo: 'Lucro Presumido' }
      case 'Autônomo':
        return { percentual: 11, valor: (valor * 11) / 100, tipo: 'ISS + INSS' }
      default:
        return { percentual: 0, valor: 0, tipo: 'Não calculado' }
    }
  }

  const getAlertasRisco = (tipoEmissor: string, valor: number, servico: string) => {
    const alertas = []
    
    if (tipoEmissor === 'MEI' && valor > 81000) {
      alertas.push('⚠️ Valor anual acima do limite MEI (R$ 81.000)')
    }
    
    if (tipoEmissor === 'MEI' && servico.toLowerCase().includes('advocacia')) {
      alertas.push('⚠️ Advocacia não é atividade permitida para MEI')
    }
    
    if (tipoEmissor === 'MEI' && servico.toLowerCase().includes('consultoria')) {
      alertas.push('⚠️ Consultoria pode ter restrições para MEI')
    }
    
    if (valor > 50000) {
      alertas.push('⚠️ Valores altos requerem atenção especial na emissão')
    }
    
    return alertas
  }

  const gerarOrientacao = async () => {
    if (!tipoEmissor || !tipoServico || !valor) {
      alert('Preencha todos os campos')
      return
    }

    setLoading(true)

    try {
      const valorNum = parseFloat(valor)
      const cnae = getCNAE(tipoServico)
      const imposto = calcularImposto(tipoEmissor, valorNum)
      const alertas = getAlertasRisco(tipoEmissor, valorNum, tipoServico)
      
      const tipoNota = tipoServico.toLowerCase().includes('produto') ? 'NF-e (Nota Fiscal Eletrônica)' : 'NFS-e (Nota Fiscal de Serviço Eletrônica)'
      
      const textoDescricao = `Prestação de serviços de ${tipoServico} conforme acordado entre as partes. Valor total: R$ ${valorNum.toFixed(2)}. Serviço executado no período de [DATA INÍCIO] até [DATA FIM].`
      
      const checklist = [
        'Emitir nota em até 5 dias após a prestação do serviço',
        'Informar valor líquido correto (após descontos)',
        'Classificar corretamente o CNAE',
        'Guardar comprovante por 5 anos',
        'Verificar retenção de impostos na fonte (se aplicável)',
        'Conferir dados do tomador do serviço'
      ]

      const orientacaoGerada = {
        tipoNota,
        cnae,
        imposto,
        textoDescricao,
        checklist,
        alertas,
        tipoEmissor,
        tipoServico,
        valor: valorNum,
        dataGeracao: new Date().toISOString()
      }

      // Salvar no banco
      const user = lumi.auth.user
      if (user) {
        await lumi.entities.configuracao_marca.create({
          userId: user.userId,
          tipo: 'orientacao_nota_fiscal',
          dados: JSON.stringify(orientacaoGerada),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }

      setOrientacao(orientacaoGerada)
      await carregarHistorico()
    } catch (error) {
      console.error('Erro ao gerar orientação:', error)
      alert('Erro ao gerar orientação')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#8B0000]">Assistente de Nota Fiscal</h1>
              <p className="text-xs text-gray-600">Orientações para emissão de nota fiscal</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Emissor *</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['MEI', 'Simples Nacional', 'Lucro Presumido', 'Autônomo'].map(tipo => (
                  <button
                    key={tipo}
                    onClick={() => setTipoEmissor(tipo)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      tipoEmissor === tipo
                        ? 'bg-[#8B0000] text-white border-[#8B0000]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-red-300'
                    }`}
                  >
                    {tipo}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Serviço/Produto *</label>
              <input
                type="text"
                value={tipoServico}
                onChange={(e) => setTipoServico(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Ex: Desenvolvimento de software, Design gráfico, Consultoria..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Valor (R$) *</label>
              <input
                type="number"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Ex: 5000.00"
              />
            </div>

            <button
              onClick={gerarOrientacao}
              disabled={loading}
              className="w-full bg-[#8B0000] text-white py-3 rounded-lg font-semibold hover:bg-[#6B0000] transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2 text-sm"
            >
              {loading ? 'Gerando Orientação...' : (
                <>
                  <FileText size={18} />
                  Gerar Orientação
                </>
              )}
            </button>

            {orientacao && (
              <div className="mt-6 space-y-4 border-t pt-6">
                {/* Tipo de nota */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-blue-900 mb-2">1. Tipo de Nota Recomendada</h3>
                  <p className="text-sm text-blue-800">{orientacao.tipoNota}</p>
                </div>

                {/* CNAE */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-purple-900 mb-2">2. Sugestão de CNAE</h3>
                  <p className="text-sm text-purple-800">{orientacao.cnae}</p>
                </div>

                {/* Simulação de imposto */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-green-900 mb-2">3. Simulação de Imposto</h3>
                  <div className="space-y-1 text-sm text-green-800">
                    <p>Regime: {orientacao.imposto.tipo}</p>
                    <p>Percentual: {orientacao.imposto.percentual}%</p>
                    <p className="font-semibold">Valor do imposto: R$ {orientacao.imposto.valor.toFixed(2)}</p>
                    <p className="font-semibold">Valor líquido: R$ {(orientacao.valor - orientacao.imposto.valor).toFixed(2)}</p>
                  </div>
                </div>

                {/* Texto pronto */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">4. Texto Pronto para Descrição</h3>
                  <p className="text-xs text-gray-700 leading-relaxed">{orientacao.textoDescricao}</p>
                  <button
                    onClick={() => navigator.clipboard.writeText(orientacao.textoDescricao)}
                    className="mt-2 text-xs text-[#8B0000] hover:underline"
                  >
                    Copiar texto
                  </button>
                </div>

                {/* Checklist */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-yellow-900 mb-3 flex items-center gap-2">
                    <CheckCircle size={16} />
                    5. Checklist Fiscal
                  </h3>
                  <ul className="space-y-2">
                    {orientacao.checklist.map((item: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-yellow-800">
                        <span className="text-yellow-600">☐</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Alertas */}
                {orientacao.alertas.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="text-sm font-bold text-red-900 mb-3 flex items-center gap-2">
                      <AlertTriangle size={16} />
                      6. Alertas de Risco
                    </h3>
                    <ul className="space-y-2">
                      {orientacao.alertas.map((alerta: string, index: number) => (
                        <li key={index} className="text-xs text-red-800">{alerta}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600">
                    ✓ Orientação salva no histórico • {new Date().toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Histórico de Orientações */}
        {historico.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-bold text-[#8B0000] mb-4 flex items-center gap-2">
              <Clock size={20} />
              Histórico de Orientações
            </h2>
            <div className="space-y-3">
              {historico.map((item) => {
                const dados = JSON.parse(item.dados)
                const isExpanded = expandido === item._id
                return (
                  <div key={item._id} className="border border-gray-200 rounded-lg">
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                      onClick={() => setExpandido(isExpanded ? null : item._id)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {dados.tipoServico} • {dados.tipoEmissor}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          R$ {dados.valor?.toFixed(2)} • {new Date(item.createdAt).toLocaleDateString('pt-BR')} às {new Date(item.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                    </div>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-bold text-blue-900 mb-1">Tipo de Nota</p>
                          <p className="text-xs text-blue-800">{dados.tipoNota}</p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <p className="text-xs font-bold text-purple-900 mb-1">CNAE</p>
                          <p className="text-xs text-purple-800">{dados.cnae}</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs font-bold text-green-900 mb-1">Imposto</p>
                          <p className="text-xs text-green-800">{dados.imposto.tipo} • {dados.imposto.percentual}% • R$ {dados.imposto.valor.toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
